import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
  getData,
  saveData,
} from "../../src/utils/localStorage/LocalStorageHandler.js";
import "./ThmeSidebar.scss";

const themes = [
  // Bootstrap4
  {
    name: "bootstrap4-light-blue",
    code: "bootstrap4-light-blue",
    color: "#42a5f5",
  },
  {
    name: "bootstrap4-light-purple",
    code: "bootstrap4-light-purple",
    color: "#ab47bc",
  },
  {
    name: "bootstrap4-dark-blue",
    code: "bootstrap4-dark-blue",
    color: "#1e88e5",
  },
  {
    name: "bootstrap4-dark-purple",
    code: "bootstrap4-dark-purple",
    color: "#8e24aa",
  },

  // Material Design
  { name: "md-light-indigo", code: "md-light-indigo", color: "#3f51b5" },
  {
    name: "md-light-deeppurple",
    code: "md-light-deeppurple",
    color: "#673ab7",
  },
  { name: "md-dark-indigo", code: "md-dark-indigo", color: "#303f9f" },
  { name: "md-dark-deeppurple", code: "md-dark-deeppurple", color: "#512da8" },

  // MDC
  { name: "mdc-light-indigo", code: "mdc-light-indigo", color: "#3f51b5" },
  {
    name: "mdc-light-deeppurple",
    code: "mdc-light-deeppurple",
    color: "#673ab7",
  },
  { name: "mdc-dark-indigo", code: "mdc-dark-indigo", color: "#303f9f" },
  {
    name: "mdc-dark-deeppurple",
    code: "mdc-dark-deeppurple",
    color: "#512da8",
  },

  // Others
  { name: "tailwind-light", code: "tailwind-light", color: "#06b6d4" },
  { name: "fluent-light", code: "fluent-light", color: "#0078d4" },

  // Lara
  { name: "lara-light-blue", code: "lara-light-blue", color: "#42a5f5" },
  { name: "lara-light-indigo", code: "lara-light-indigo", color: "#3949ab" },
  { name: "lara-light-purple", code: "lara-light-purple", color: "#8e24aa" },
  { name: "lara-light-teal", code: "lara-light-teal", color: "#00897b" },
  { name: "lara-dark-blue", code: "lara-dark-blue", color: "#1565c0" },
  { name: "lara-dark-indigo", code: "lara-dark-indigo", color: "#283593" },
  { name: "lara-dark-purple", code: "lara-dark-purple", color: "#6a1b9a" },
  { name: "lara-dark-teal", code: "lara-dark-teal", color: "#00695c" },

  // Soho
  { name: "soho-light", code: "soho-light", color: "#26c6da" },
  { name: "soho-dark", code: "soho-dark", color: "#00838f" },

  // Viva
  { name: "viva-light", code: "viva-light", color: "#ff7043" },
  { name: "viva-dark", code: "viva-dark", color: "#e64a19" },

  // Other bases
  { name: "mira", code: "mira", color: "#009688" },
  { name: "nano", code: "nano", color: "#00bcd4" },

  // Saga
  { name: "saga-blue", code: "saga-blue", color: "#2196f3" },
  { name: "saga-green", code: "saga-green", color: "#43a047" },
  { name: "saga-orange", code: "saga-orange", color: "#fb8c00" },
  { name: "saga-purple", code: "saga-purple", color: "#8e24aa" },

  // Vela
  { name: "vela-blue", code: "vela-blue", color: "#2196f3" },
  { name: "vela-green", code: "vela-green", color: "#43a047" },
  { name: "vela-orange", code: "vela-orange", color: "#fb8c00" },
  { name: "vela-purple", code: "vela-purple", color: "#8e24aa" },

  // Arya
  { name: "arya-blue", code: "arya-blue", color: "#1e88e5" },
  { name: "arya-green", code: "arya-green", color: "#43a047" },
  { name: "arya-orange", code: "arya-orange", color: "#f57c00" },
  { name: "arya-purple", code: "arya-purple", color: "#8e24aa" },
];

const ThemeSidebar = ({ visible, onHide }) => {
  const [theme, setTheme] = useState(null);

  // load theme css from UNPKG (same as dropdown)
  const loadTheme = (themeCode) => {
    let link = document.getElementById("app-theme");
    if (!link) {
      link = document.createElement("link");
      link.id = "app-theme";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `https://unpkg.com/primereact/resources/themes/${themeCode}/theme.css`;
  };

  useEffect(() => {
    const savedTheme = getData("app-theme");
    if (savedTheme) {
      setTheme(savedTheme);
      loadTheme(savedTheme);
    } else {
      setTheme(themes[0].code);
      loadTheme(themes[0].code);
    }
  }, []);

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    saveData("app-theme", newTheme);
    loadTheme(newTheme);
    onHide();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`theme-overlay ${visible ? "show" : ""}`}
        role="button"
        tabIndex={0}
        onClick={onHide}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onHide();
        }}
        aria-label="Close Theme Sidebar"
      />

      {/* Sidebar */}
      <div className={`theme-sidebar ${visible ? "show" : ""}`}>
        <div className="theme-header">
          <h3>Select Theme</h3>
          <button className="close-btn" onClick={onHide}>
            ✖
          </button>
        </div>

        <div className="theme-grid">
          {themes.map((t) => {
            const isDark = t.code.includes("dark");

            return (
              <div
                key={t.code}
                role="button"
                tabIndex={0}
                className={`theme-card ${theme === t.code ? "active" : ""} ${isDark ? "dark" : "light"}`}
                onClick={() => changeTheme(t.code)}
              >
                {/* Theme Preview Box */}
                <div className="theme-preview">
                  <div
                    className="theme-preview-header"
                    style={{ backgroundColor: t.color }}
                  ></div>
                  <div className="theme-preview-body">
                    <div
                      className="sidebar"
                      style={{
                        backgroundColor: isDark ? "#444" : t.color + "33",
                      }}
                    ></div>
                    <div
                      className="content"
                      style={{ backgroundColor: isDark ? "#2d2d2d" : "#fff" }}
                    ></div>
                  </div>
                </div>
                <span>{t.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

ThemeSidebar.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ThemeSidebar;
